// Pointer-event gesture state machine for the gallery image stage.
// Four modes: idle / swiping / panning / zooming. The host passes
// raw pointer events in via pointerDown / pointerMove / pointerUp
// and receives semantic callbacks (swipe, pan, zoom change, close
// request, double-tap zoom).
//
// Zoom locks horizontal swipe: while zoomed > 1, horizontal drag
// pans the image; at zoom == 1, horizontal drag navigates the
// carousel. Vertical drag down past threshold always requests
// close (only when zoom == 1).
//
// Per-pointer we track two samples: the pointer-down position
// (for swipe/close thresholds and tap detection) and the latest
// position (for incremental pan deltas). Real touch hardware emits
// many small pointermoves, so threshold checks against "delta since
// the previous move" never fire — they must compare against
// "delta since pointerdown".

import { useCallback, useRef } from "react";

export interface PointerSample {
  id: number;
  x: number;
  y: number;
  time?: number;
}

export interface UseGesturesOptions {
  onSwipe?: (direction: "left" | "right") => void;
  onPan?: (dx: number, dy: number) => void;
  onZoomChange?: (newZoom: number, centerX: number, centerY: number) => void;
  onCloseRequest?: () => void;
  onDoubleTapZoom?: (x: number, y: number) => void;
  initialZoom?: number;
  swipeThreshold?: number;
  closeThreshold?: number;
  doubleTapMs?: number;
}

interface PointerTrack {
  down: PointerSample;
  current: PointerSample;
}

interface Active {
  pointers: Map<number, PointerTrack>;
  startPinchDistance: number | null;
  startPinchZoom: number;
  lastTapTime: number;
  swipeCommitted: boolean;
  closeCommitted: boolean;
}

export function useGestures(opts: UseGesturesOptions) {
  const {
    onSwipe,
    onPan,
    onZoomChange,
    onCloseRequest,
    onDoubleTapZoom,
    initialZoom = 1,
    swipeThreshold = 80,
    closeThreshold = 120,
    doubleTapMs = 300,
  } = opts;

  const zoomRef = useRef(initialZoom);
  const activeRef = useRef<Active>({
    pointers: new Map(),
    startPinchDistance: null,
    startPinchZoom: initialZoom,
    lastTapTime: 0,
    swipeCommitted: false,
    closeCommitted: false,
  });

  const pointerDown = useCallback((p: PointerSample) => {
    const a = activeRef.current;
    a.pointers.set(p.id, { down: p, current: p });
    a.swipeCommitted = false;
    a.closeCommitted = false;
    if (a.pointers.size === 2) {
      const [t1, t2] = [...a.pointers.values()];
      a.startPinchDistance = dist(t1.current, t2.current);
      a.startPinchZoom = zoomRef.current;
    }
  }, []);

  const pointerMove = useCallback(
    (p: PointerSample) => {
      const a = activeRef.current;
      const track = a.pointers.get(p.id);
      if (!track) return;
      const prev = track.current;
      track.current = p;

      // Pinch mode (two pointers).
      if (a.pointers.size === 2 && a.startPinchDistance) {
        const [t1, t2] = [...a.pointers.values()];
        const d = dist(t1.current, t2.current);
        if (d > 0) {
          const newZoom = clamp(
            (d / a.startPinchDistance) * a.startPinchZoom,
            1,
            8,
          );
          zoomRef.current = newZoom;
          const cx = (t1.current.x + t2.current.x) / 2;
          const cy = (t1.current.y + t2.current.y) / 2;
          onZoomChange?.(newZoom, cx, cy);
        }
        return;
      }

      // Single-pointer mode.
      if (a.pointers.size !== 1) return;

      if (zoomRef.current > 1) {
        // Pan consumes incremental deltas — the caller accumulates
        // them into absolute pan state.
        onPan?.(p.x - prev.x, p.y - prev.y);
        return;
      }

      // Swipe and close thresholds compare against the pointer-down
      // origin, NOT the previous move. Real touch input fires many
      // small incremental pointermoves, so a per-move delta would
      // never cross an 80 px threshold.
      const totalDx = p.x - track.down.x;
      const totalDy = p.y - track.down.y;

      if (
        !a.closeCommitted &&
        totalDy > closeThreshold &&
        Math.abs(totalDy) > Math.abs(totalDx)
      ) {
        a.closeCommitted = true;
        onCloseRequest?.();
        return;
      }
      if (
        !a.swipeCommitted &&
        Math.abs(totalDx) > swipeThreshold &&
        Math.abs(totalDx) > Math.abs(totalDy)
      ) {
        a.swipeCommitted = true;
        onSwipe?.(totalDx > 0 ? "right" : "left");
      }
    },
    [onSwipe, onPan, onZoomChange, onCloseRequest, swipeThreshold, closeThreshold],
  );

  const pointerUp = useCallback(
    (p: PointerSample) => {
      const a = activeRef.current;
      const track = a.pointers.get(p.id);
      a.pointers.delete(p.id);

      if (a.pointers.size < 2) {
        a.startPinchDistance = null;
      }

      // Double-tap detection: release must be close to the
      // pointer-down origin (not to the last move), and must not
      // have committed a swipe or close gesture first.
      if (
        track &&
        !a.swipeCommitted &&
        !a.closeCommitted &&
        Math.hypot(p.x - track.down.x, p.y - track.down.y) < 10
      ) {
        const now = p.time ?? Date.now();
        if (now - a.lastTapTime < doubleTapMs) {
          onDoubleTapZoom?.(p.x, p.y);
          a.lastTapTime = 0;
        } else {
          a.lastTapTime = now;
        }
      }
    },
    [onDoubleTapZoom, doubleTapMs],
  );

  const setZoom = useCallback((z: number) => {
    zoomRef.current = z;
  }, []);

  return { pointerDown, pointerMove, pointerUp, setZoom };
}

function dist(a: PointerSample, b: PointerSample): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
